import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, normalizeStyle as _normalizeStyle, renderList as _renderList, Fragment as _Fragment, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = ["disabled"]
const _hoisted_2 = {
  key: 1,
  class: "ncr-file-progress"
}
const _hoisted_3 = {
  key: 2,
  class: "ncr-pane-alert is-error"
}
const _hoisted_4 = {
  key: 3,
  class: "ncr-pane-empty"
}
const _hoisted_5 = {
  key: 4,
  class: "ncr-files-list"
}
const _hoisted_6 = { class: "ncr-file-type" }
const _hoisted_7 = ["onClick"]
const _hoisted_8 = ["onClick"]
const _hoisted_9 = ["href"]
const _hoisted_10 = ["onClick"]

import { collection, onSnapshot, orderBy, query, type Unsubscribe } from 'firebase/firestore';
import { computed, onBeforeUnmount, ref, watch } from 'vue';

import { db } from '@/modules/firebase';
import { fileClient, type RecordFile } from '@/modules/common/files/FileClient';
import {
  uploadRecordFiles,
  validateLocalRecordFile
} from '@/modules/common/files/RecordFileWorkflow';
import type { LegacyNcrFile } from '@/modules/ncr/NcrClient';
import { ncrClient } from '@/modules/ncr/NcrClient';


export default /*@__PURE__*/_defineComponent({
  __name: 'NcrFilesPane',
  props: {
    recordId: {},
    legacyFiles: {},
    readonly: { type: Boolean }
  },
  emits: ["remove-legacy"],
  setup(__props: any) {

const props = __props;



const modernFiles = ref<RecordFile[]>([]);
const uploadInput = ref<HTMLInputElement | null>(null);
const isUploading = ref(false);
const uploadProgress = ref(0);
const uploadLabel = ref('');
const errorMessage = ref('');
let unsubscribe: Unsubscribe | null = null;
const visibleLegacyFiles = computed(() => props.legacyFiles.filter(file => !file.sourceFileId));

function timestampToMillis(value: any): number {
  if (typeof value?.toMillis === 'function') return value.toMillis();
  if (typeof value?.seconds === 'number') return value.seconds * 1000;
  return typeof value === 'number' ? value : 0;
}

function subscribe(recordId: string): void {
  unsubscribe?.();
  unsubscribe = null;
  modernFiles.value = [];
  if (!recordId) return;

  unsubscribe = onSnapshot(
    query(collection(db, 'ncr', recordId, 'files'), orderBy('createdAt', 'desc')),
    snapshot => {
      modernFiles.value = snapshot.docs
        .map(entry => {
          const value = entry.data();
          return {
            id: entry.id,
            ...value,
            createdAt: timestampToMillis(value.createdAt),
            updatedAt: timestampToMillis(value.updatedAt)
          } as RecordFile;
        })
        .filter(file => !file.deleted && file.status !== 'deleted');
    },
    error => {
      errorMessage.value = error instanceof Error ? error.message : 'Failed to load files';
    }
  );
}

async function upload(files: File[]): Promise<void> {
  if (props.readonly || !props.recordId || !files.length || isUploading.value) return;
  for (const file of files) {
    const validationError = validateLocalRecordFile(file);
    if (validationError) {
      errorMessage.value = validationError;
      return;
    }
  }

  isUploading.value = true;
  errorMessage.value = '';
  uploadProgress.value = 0;
  try {
    const uploadedFiles = await uploadRecordFiles({
      recordType: 'ncr',
      recordId: props.recordId,
      files,
      onProgress: progress => {
        uploadProgress.value = progress.percent;
        uploadLabel.value = `Uploading ${progress.fileName} (${progress.percent}%)`;
      }
    });
    for (const uploadedFile of uploadedFiles) {
      const downloadUrl = await fileClient.getDownloadUrl(uploadedFile.storagePath);
      await ncrClient.addModernFileMirror(props.recordId, uploadedFile, downloadUrl);
    }
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : 'File upload failed';
  } finally {
    isUploading.value = false;
    uploadLabel.value = '';
    if (uploadInput.value) uploadInput.value.value = '';
  }
}

function handleInputChange(event: Event): void {
  const files = Array.from((event.target as HTMLInputElement).files || []);
  void upload(files);
}

function handleDrop(event: DragEvent): void {
  void upload(Array.from(event.dataTransfer?.files || []));
}

async function downloadModern(file: RecordFile): Promise<void> {
  try {
    const url = await fileClient.getDownloadUrl(file.storagePath);
    window.open(url, '_blank', 'noopener');
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : 'Failed to open file';
  }
}

async function removeModern(file: RecordFile): Promise<void> {
  if (!window.confirm(`Delete ${file.displayName}?`)) return;
  try {
    await fileClient.deleteFile('ncr', props.recordId, file.id);
    await ncrClient.removeModernFileMirror(props.recordId, file.id, props.legacyFiles);
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : 'Failed to delete file';
  }
}

watch(() => props.recordId, subscribe, { immediate: true });
onBeforeUnmount(() => unsubscribe?.());

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("section", {
    class: "ncr-files-pane",
    onDragover: _cache[1] || (_cache[1] = _withModifiers(() => {}, ["prevent"])),
    onDrop: _withModifiers(handleDrop, ["prevent"])
  }, [
    (!__props.readonly)
      ? (_openBlock(), _createElementBlock("button", {
          key: 0,
          type: "button",
          class: "ncr-files-dropzone",
          disabled: !__props.recordId || isUploading.value,
          onClick: _cache[0] || (_cache[0] = ($event: any) => (uploadInput.value?.click()))
        }, [...(_cache[2] || (_cache[2] = [
          _createElementVNode("i", { class: "material-icons" }, "attach_file", -1 /* CACHED */),
          _createElementVNode("span", null, [
            _createTextVNode("Drag and Drop or "),
            _createElementVNode("strong", null, "Choose File")
          ], -1 /* CACHED */),
          _createElementVNode("small", null, "Images, PDF, XLSX, CSV, or TXT. Maximum 10 MB.", -1 /* CACHED */)
        ]))], 8 /* PROPS */, _hoisted_1))
      : _createCommentVNode("v-if", true),
    _createElementVNode("input", {
      ref_key: "uploadInput",
      ref: uploadInput,
      type: "file",
      class: "ncr-files-hidden-input",
      multiple: "",
      hidden: "",
      onChange: handleInputChange
    }, null, 544 /* NEED_HYDRATION, NEED_PATCH */),
    (isUploading.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createElementVNode("span", null, _toDisplayString(uploadLabel.value), 1 /* TEXT */),
          _createElementVNode("div", null, [
            _createElementVNode("span", {
              style: _normalizeStyle({ width: `${uploadProgress.value}%` })
            }, null, 4 /* STYLE */)
          ])
        ]))
      : _createCommentVNode("v-if", true),
    (errorMessage.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_3, _toDisplayString(errorMessage.value), 1 /* TEXT */))
      : _createCommentVNode("v-if", true),
    (!modernFiles.value.length && !visibleLegacyFiles.value.length)
      ? (_openBlock(), _createElementBlock("div", _hoisted_4, "No files attached yet."))
      : (_openBlock(), _createElementBlock("div", _hoisted_5, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(modernFiles.value, (file) => {
            return (_openBlock(), _createElementBlock("article", {
              key: file.id,
              class: "ncr-file-row"
            }, [
              _createElementVNode("span", _hoisted_6, _toDisplayString(file.extension.toUpperCase() || 'FILE'), 1 /* TEXT */),
              _createElementVNode("button", {
                type: "button",
                class: "ncr-file-name",
                onClick: ($event: any) => (downloadModern(file))
              }, _toDisplayString(file.displayName), 9 /* TEXT, PROPS */, _hoisted_7),
              _createElementVNode("small", null, "v" + _toDisplayString(file.version), 1 /* TEXT */),
              (!__props.readonly)
                ? (_openBlock(), _createElementBlock("button", {
                    key: 0,
                    type: "button",
                    class: "button pill -text",
                    "aria-label": "Delete file",
                    onClick: ($event: any) => (removeModern(file))
                  }, [...(_cache[3] || (_cache[3] = [
                    _createElementVNode("i", { class: "material-icons" }, "delete", -1 /* CACHED */)
                  ]))], 8 /* PROPS */, _hoisted_8))
                : _createCommentVNode("v-if", true)
            ]))
          }), 128 /* KEYED_FRAGMENT */)),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visibleLegacyFiles.value, (file) => {
            return (_openBlock(), _createElementBlock("article", {
              key: `${file.name}-${file.path}`,
              class: "ncr-file-row is-legacy"
            }, [
              _cache[5] || (_cache[5] = _createElementVNode("span", { class: "ncr-file-type" }, "FILE", -1 /* CACHED */)),
              _createElementVNode("a", {
                class: "ncr-file-name",
                href: file.path,
                target: "_blank",
                rel: "noopener"
              }, _toDisplayString(file.name), 9 /* TEXT, PROPS */, _hoisted_9),
              _cache[6] || (_cache[6] = _createElementVNode("small", null, "Legacy", -1 /* CACHED */)),
              (!__props.readonly)
                ? (_openBlock(), _createElementBlock("button", {
                    key: 0,
                    type: "button",
                    class: "button pill -text",
                    "aria-label": "Delete legacy file",
                    onClick: ($event: any) => (_ctx.$emit('remove-legacy', file))
                  }, [...(_cache[4] || (_cache[4] = [
                    _createElementVNode("i", { class: "material-icons" }, "delete", -1 /* CACHED */)
                  ]))], 8 /* PROPS */, _hoisted_10))
                : _createCommentVNode("v-if", true)
            ]))
          }), 128 /* KEYED_FRAGMENT */))
        ]))
  ], 32 /* NEED_HYDRATION */))
}
}

})